import { useEffect, useState } from "react";
import {
  Button,
  Row,
  Layout,
  Input,
  Form,
  message,
  Typography,
  Popconfirm,
  Breadcrumb,
  Col,
  Card,
  Select,
} from "antd";
import { DeleteOutlined, InboxOutlined, DownloadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import {
  corpus_create,
  corpus_update,
  corpus_getById,
  corpus_uploadFile,
} from "../../services/copus";
import EditorContentPage from "../../components/Editor";
import { useTranslation } from "react-i18next";
import Dragger from "antd/es/upload/Dragger";

const { Content } = Layout;

const CreateCorpuspage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [editorContent, setEditorContent] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [fileList, setFileList] = useState<any[]>([]);
  const [fileCorpusNames, setFileCorpusNames] = useState<{
    [key: string]: string;
  }>({});

  // create/update corpus
  const handleCreateAbout = async (value: any) => {
    setSubmitting(true);

    try {
      // เตรียมข้อมูลตาม Backend Model
      const corpusData = {
        title: value.title,
        description: editorContent || "",
        owner_name: value.owner_name,
        owner_contact: value.owner_contact || "",
        source_reference: value.source_reference,
        publish_year: parseInt(value.publish_year),
        publish_status: 0,
        external_link: "",
        file_path: "",
      };

      let corpus_id = params.id || "";

      if (params.action === "create") {
        // 1. สร้าง Corpus ก่อน
        const createResponse = await corpus_create(corpusData);

        if (createResponse.status === "success") {
          corpus_id = createResponse.corpus_id;

          // 2. Upload files
          if (fileList.length > 0) {
            let allFilePaths: any[] = [];

            for (const file of fileList) {
              try {
                const fileToUpload = file.originFileObj || file;
                const corpusName = fileCorpusNames[file.uid] || file.name;

                const uploadResponse = await corpus_uploadFile({
                  corpus_id: corpus_id,
                  file: fileToUpload,
                  name: corpusName,
                });

                if (uploadResponse.status === "success" && uploadResponse.path) {
                  const sizeBytes = typeof fileToUpload?.size === "number" ? fileToUpload.size : null;
                  allFilePaths.push({
                    id: file.uid,
                    name: corpusName,
                    filepath: uploadResponse.path,
                    filesize: sizeBytes,
                    filesize_mb: sizeBytes != null ? Number((sizeBytes / (1024 * 1024)).toFixed(2)) : null,
                  });
                }
              } catch (uploadError) {
                console.error("Error uploading file:", uploadError);
                message.warning(`ไม่สามารถอัพโหลดไฟล์ ${file.name} ได้`);
              }
            }

            // Persist file_path on create as well
            if (allFilePaths.length > 0) {
              corpusData.file_path = JSON.stringify(allFilePaths);
            }
          }

          message.success("Submit Success!");
          navigate("/corpus");
        }
      } else if (params.action === "edit" && params.id) {
        // Update existing corpus
        let allFilePaths: any[] = [];

        // รวบรวมไฟล์ทั้งหมด (เก่า + ใหม่)
        for (const file of fileList) {
          // ถ้าเป็นไฟล์เก่าที่มีอยู่แล้ว
          if (file.isExisting) {
            const sizeBytes = typeof file.size === "number" ? file.size : null;
            allFilePaths.push({
              id: file.uid,
              name: fileCorpusNames[file.uid] || file.name,
              filepath: file.url,
              filesize: sizeBytes,
              filesize_mb: sizeBytes != null ? Number((sizeBytes / (1024 * 1024)).toFixed(2)) : null,
            });
          }
          // ถ้าเป็นไฟล์ใหม่ที่ต้องอัปโหลด
          else {
            try {
              const fileToUpload = file.originFileObj || file;
              const corpusName = fileCorpusNames[file.uid] || file.name;

              const uploadResponse = await corpus_uploadFile({
                corpus_id: params.id,
                file: fileToUpload,
                name: corpusName,
              });

              if (uploadResponse.status === "success" && uploadResponse.path) {
                const sizeBytes = typeof fileToUpload?.size === "number" ? fileToUpload.size : null;
                allFilePaths.push({
                  id: file.uid,
                  name: corpusName,
                  filepath: uploadResponse.path,
                  filesize: sizeBytes,
                  filesize_mb: sizeBytes != null ? Number((sizeBytes / (1024 * 1024)).toFixed(2)) : null,
                });
              }
            } catch (uploadError) {
              console.error("Error uploading file:", uploadError);
              message.warning(`ไม่สามารถอัพโหลดไฟล์ ${file.name} ได้`);
            }
          }
        }

        // อัปเดต file_path ด้วยไฟล์ทั้งหมด
        if (allFilePaths.length > 0) {
          corpusData.file_path = JSON.stringify(allFilePaths);
        }

        const updateResponse = await corpus_update(params.id, corpusData);

        if (updateResponse.status === "success") {
          message.success("Update Success!");
          navigate("/corpus");
        }
      }
    } catch (e) {
      console.error(e);
      message.error("เกิดข้อผิดพลาดในการบันทึกข้อมูล");
    }
    setSubmitting(false);
  };

  useEffect(() => {
    const fetchData = async () => {
      if (params.action === "edit" && params.id) {
        await getAboutDetail();
      }
    };
    fetchData();
  }, [params.action, params.id]);

  const getAboutDetail = async () => {
    try {
      const response = await corpus_getById({ corpus_id: params.id });
      if (response.result?.[0]) {
        const data = response.result[0];
        // console.log("API description →", data.description);

        form.setFieldsValue({
          title: data.title || "",
          owner_name: data.owner_name || "",
          owner_contact: data.owner_contact || "",
          publish_year: data.publish_year ? String(data.publish_year) : "",
          source_reference: data.source_reference || "",
        });

        setEditorContent(data.description || "");
        // โหลดไฟล์ที่มีอยู่แล้ว
        if (data.file_path) {
          try {
            const files = JSON.parse(data.file_path);
            if (Array.isArray(files) && files.length > 0) {
              const existingFiles = files.map(
                (fileInfo: any, index: number) => ({
                  uid: fileInfo.id || `existing-${index}`,
                  name: fileInfo.name || `File ${index + 1}`,
                  status: "done",
                  url: fileInfo.filepath,
                  // Ant Upload uses `size` (bytes). If old records don't have it, leave undefined.
                  size: typeof fileInfo.filesize === "number" ? fileInfo.filesize : undefined,
                  isExisting: true,
                }),
              );

              setFileList(existingFiles);

              const names: { [key: string]: string } = {};
              files.forEach((fileInfo: any, index: number) => {
                const uid = fileInfo.id || `existing-${index}`;
                names[uid] = fileInfo.name || "";
              });
              setFileCorpusNames(names);
            }
          } catch (parseError) {
            console.error("Error parsing file_path:", parseError);
          }
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleFileChange = (info: any) => {
    let newFileList = [...info.fileList];
    setFileList(newFileList);
  };

  const handleCorpusNameChange = (fileUid: string, name: string) => {
    setFileCorpusNames((prev) => ({
      ...prev,
      [fileUid]: name,
    }));
  };

  const handleRemoveFile = (file: any) => {
    const newFileList = fileList.filter((item) => item.uid !== file.uid);
    setFileList(newFileList);

    // Remove corpus name for this file
    const newFileCorpusNames = { ...fileCorpusNames };
    delete newFileCorpusNames[file.uid];
    setFileCorpusNames(newFileCorpusNames);
  };

  return (
    <div>
      <Content className="create-about-layout">
        <div>
          <Row justify="space-between" align="middle">
            <Breadcrumb
              separator=">"
              items={[
                {
                  title: (
                    <span style={{ fontWeight: "bold" }}>{t("Corpus")}</span>
                  ),
                },
              ]}
            />
          </Row>
          <hr />
        </div>
      </Content>

      <Content style={{ padding: "0px 30px 20px 30px" }}>
        <Form
          form={form}
          name="image-upload-form"
          onFinish={handleCreateAbout}
          layout="vertical"
          style={{
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "24px 0px 24px 0px",
          }}
        >
          <Form.Item>
            <Dragger
              name="file"
              multiple={true}
              fileList={fileList}
              onChange={handleFileChange}
              beforeUpload={() => false}
            >
              <p className="ant-upload-drag-icon">
                <InboxOutlined style={{ color: "#1890ff", fontSize: "48px" }} />
              </p>
              <p
                className="ant-upload-text"
                style={{ fontSize: "16px", fontWeight: "500" }}
              >
                คลิกหรือลากไฟล์มาที่นี่เพื่ออัพโหลด
              </p>
              <p className="ant-upload-hint" style={{ color: "#8c8c8c" }}>
                รองรับการอัพโหลดหลายไฟล์พร้อมกัน
              </p>
            </Dragger>
          </Form.Item>

          {fileList.length > 0 && (
            <Card title="รายการไฟล์ที่เลือก" style={{ marginBottom: "24px" }}>
              {fileList.map((file, index) => (
                <Row
                  key={file.uid}
                  gutter={16}
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    borderRadius: "4px",
                  }}
                  align="middle"
                >
                  <Col span={8}>
                    <Typography.Text strong>
                      ไฟล์ที่ {index + 1}: {file.name}
                    </Typography.Text>
                  </Col>
                  <Col span={14}>
                    <Input
                      placeholder={`ตั้งชื่อ Corpus สำหรับไฟล์ ${file.name}`}
                      value={fileCorpusNames[file.uid] || ""}
                      onChange={(e) =>
                        handleCorpusNameChange(file.uid, e.target.value)
                      }
                    />
                  </Col>
                  <Col span={2}>
                    <Button
                      type="text"
                      danger
                      icon={<DeleteOutlined />}
                      onClick={() => handleRemoveFile(file)}
                    />
                  </Col>
                </Row>
              ))}
            </Card>
          )}

          <Form.Item
            name="title"
            label={t("Title")}
            rules={[
              { required: true, message: "Please input the title!" },
              {
                validator: (_, value) =>
                  value && value.trim() !== ""
                    ? Promise.resolve()
                    : Promise.reject(new Error("กรุณากรอกข้อมูล")),
              },
            ]}
          >
            <Input placeholder="Title" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="owner_name"
                label={t("Owner Name")}
                rules={[
                  { required: true, message: "Please input the owner name!" },
                  {
                    validator: (_, value) =>
                      value && value.trim() !== ""
                        ? Promise.resolve()
                        : Promise.reject(new Error("กรุณากรอกข้อมูล")),
                  },
                ]}
              >
                <Input placeholder="Owner Name" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item name="owner_contact" label={t("Owner Contact")}>
                <Input placeholder="Owner Contact" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="publish_year"
                label={t("Publish Year")}
                rules={[
                  { required: true, message: "Please input the publish year!" },
                  {
                    validator: (_, value) => {
                      const stringValue = String(value || "").trim();
                      if (!stringValue) {
                        return Promise.reject(new Error("กรุณากรอกข้อมูล"));
                      }
                      if (!/^\d+$/.test(stringValue)) {
                        return Promise.reject(
                          new Error("กรุณากรอกเฉพาะตัวเลข"),
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <Input placeholder="Publish Year" />
              </Form.Item>
            </Col>

            <Col span={12}>
              <Form.Item
                name="source_reference"
                label={t("Source Reference")}
                rules={[
                  {
                    required: true,
                    message: "Please input the source reference!",
                  },
                  {
                    validator: (_, value) =>
                      value && value.trim() !== ""
                        ? Promise.resolve()
                        : Promise.reject(new Error("กรุณากรอกข้อมูล")),
                  },
                ]}
              >
                <Input placeholder="Source Reference" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item label={t("Details Corpus")}>
            <EditorContentPage
              key={params.id || "new"}
              value={editorContent}
              onChange={setEditorContent}
            />
          </Form.Item>


          {/* submit */}
          <Form.Item>
            <Popconfirm
              title={
                params.action === "create"
                  ? t("Alert confirm create title")
                  : t("Alert confirm update title")
              }
              description={
                params.action === "create"
                  ? t("Alert confirm create detail")
                  : t("Alert confirm update detail")
              }
              onConfirm={() => form.submit()}
              okText={t("yes")}
              cancelText={t("no")}
            >
              <Button
                type="primary"
                htmlType="button"
                loading={submitting}
                style={{ float: "right", width: "100px" }}
              >
                {params.action === "create" ? t("create") : t("update")}
              </Button>
            </Popconfirm>

            <Popconfirm
              title={t("Alert title")}
              description={t(
                "Are you sure you want to cancel and return to the corpus page",
              )}
              onConfirm={() => navigate("/corpus")}
              okText={t("yes")}
              cancelText={t("no")}
            >
              <Button
                style={{ marginRight: "10px", float: "right", width: "100px" }}
              >
                {t("cancel")}
              </Button>
            </Popconfirm>
          </Form.Item>
        </Form>
      </Content>
    </div>
  );
};

export default CreateCorpuspage;
